import { bandDef } from "@/components/admission/bandDefinitions";
import { useState } from "react";
import { HelpCircle, ChevronDown, ChevronUp } from "lucide-react";

/**
 * ChanceLegend
 *
 * Explains what Likely / Target / Ambitious / Highly Ambitious mean so
 * students know whether to apply, and how to balance a healthy application
 * portfolio. Renders collapsible by default so it doesn't dominate the page.
 *
 * Props:
 *   defaultOpen?: boolean  — start expanded (default: false)
 *   compact?: boolean      — inline compact variant for narrow columns
 *   testId?: string        — data-testid for the wrapper
 */
export function ChanceLegend({ defaultOpen = false, compact = false, testId = "chance-legend" }) {
  const [open, setOpen] = useState(defaultOpen);

  const rows = [ // label/aka/meaning shared with the Grade match popover (BAND_DEFS)
    {
      bucket: "likely",
      badge: "chance-likely",
      label: "Likely",
      aka: bandDef("likely").pill,
      meaning: bandDef("likely").meaning,
      action: "Include 1–2 as insurance — a spot you'd happily take if reaches fall through.",
    },
    {
      bucket: "target",
      badge: "chance-target",
      label: "Target",
      aka: bandDef("target").pill,
      meaning: bandDef("target").meaning,
      action: "The workhorse of your list. Aim for 4–6 Targets.",
    },
    {
      bucket: "ambitious",
      badge: "chance-ambitious",
      label: "Ambitious",
      aka: bandDef("ambitious").pill,
      meaning: bandDef("ambitious").meaning,
      action: "Absolutely worth applying if you love the programme. 2–3 Reaches is healthy.",
    },
    {
      bucket: "highly_ambitious",
      badge: "chance-highly_ambitious",
      label: "Highly Ambitious",
      aka: bandDef("highly_ambitious").pill,
      meaning: bandDef("highly_ambitious").meaning,
      action: "Only apply if you have a compelling story (major test retake planned, unusual portfolio).",
    },
  ];

  return (
    <div
      data-testid={testId}
      className="border border-stone-200 bg-white rounded-md">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testId}-toggle`}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-stone-50 transition-colors">
        <div className="flex items-center gap-2">
          <HelpCircle className="h-4 w-4 text-stone-500" />
          <span className="label-uppercase !text-[10px] text-stone-700">
            What do Likely, Target, Ambitious mean?
          </span>
        </div>
        {open
          ? <ChevronUp className="h-4 w-4 text-stone-500" />
          : <ChevronDown className="h-4 w-4 text-stone-500" />}
      </button>

      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-stone-100" data-testid={`${testId}-body`}>
          <div className={`mt-3 grid gap-3 ${compact ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
            {rows.map((r) => (
              <div
                key={r.bucket}
                data-testid={`${testId}-row-${r.bucket}`}
                className="border border-stone-200 rounded-md p-3">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`${r.badge} text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1.5 font-mono font-semibold uppercase tracking-wider`}>
                    <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-60" />
                    {r.label}
                  </span>
                  <span className="text-xs font-mono text-stone-500">shown as {r.aka}</span>
                </div>
                <div className="text-sm text-stone-700">{r.meaning}</div>
                <div className="text-sm text-stone-600 italic mt-1">→ {r.action}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 rounded-md bg-[#F3F1EC] border border-stone-200 px-4 py-3 text-sm text-stone-800">
            <span className="label-uppercase !text-[10px] text-stone-600 block mb-1">
              A healthy shortlist
            </span>
            <span className="font-mono">2 Likely · 4–6 Target · 2–3 Ambitious · at most 1 Highly Ambitious</span>
            <div className="text-xs text-stone-600 mt-1">
              Applying to 8–12 programmes total balances safety with ambition. Every extra
              application eats real time on essays — pick fewer, apply harder.
            </div>
          </div>

          <div
            className="mt-3 rounded-md border border-stone-200 bg-white px-4 py-3"
            data-testid={`${testId}-gap-score`}
          >
            <span className="label-uppercase !text-[10px] text-stone-600 block mb-2">
              How the gap score maps to buckets
            </span>
            <div className="text-xs text-stone-700 space-y-1 font-mono">
              <div><span className="text-emerald-700">Likely</span>       — gap score ≥ +0.50 (comfortably above typical)</div>
              <div><span className="text-amber-700">Target</span>         — gap score ≥ −0.15 (around typical)</div>
              <div><span className="text-orange-700">Ambitious</span>     — gap score ≥ −0.60 (below typical, above min)</div>
              <div><span className="text-red-700">Highly Ambitious</span> — gap score &lt; −0.60 (below published min)</div>
            </div>
            <div className="mt-2 text-[11px] text-stone-500 leading-relaxed">
              The score is a weighted, rule-based blend of grades (30%),
              subject requirements (30%), test scores (15%), English (10%),
              activities (10%) and portfolio/interview (5%). It is not a
              statistical probability — it's a transparent way of comparing
              your profile to what the university publishes.
            </div>
            <a
              href="/methodology#chance-score"
              data-testid={`${testId}-methodology-link`}
              className="mt-2 inline-flex items-center gap-1 text-[11px] font-underground-sc tracking-wider text-[#4C1D95] hover:underline"
            >
              Read the full methodology →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChanceLegend;
